import { db, type DeckRow } from "./db";
import type { LanguageId } from "./references";

/**
 * The "My Words" collection is not a separate storage mechanism — it's an
 * ordinary {@link DeckRow}, lazily created the first time the user quick-adds
 * a word for a given (primary, learning) language pair, so it shows up in
 * Decks/DeckView exactly like any deck the user created by hand. The id is
 * deterministic so repeated quick-adds for the same pair reuse one deck
 * instead of creating a new one every time, and so it stays the same
 * regardless of which of the two languages a given word was entered in.
 */
export function defaultWordsDeckId(primaryLang: LanguageId, learningLang: LanguageId): string {
  return `default:${primaryLang}:${learningLang}`;
}

/**
 * Ensure the lazy "My Words" deck for (primaryLang, learningLang) exists,
 * creating it on first use. Idempotent — a second call for the same pair
 * is a no-op and returns the same id. Callers own sync enqueueing (only
 * needed when `created` is true).
 */
export async function ensureDefaultWordsDeck(
  primaryLang: LanguageId,
  learningLang: LanguageId,
  name: string,
): Promise<{ id: string; created: boolean }> {
  const id = defaultWordsDeckId(primaryLang, learningLang);
  const existing = await db.decks.get(id);
  if (existing) return { id, created: false };
  const row: DeckRow = {
    id,
    name,
    createdAt: Date.now(),
    sourceLang: learningLang,
    targetLang: primaryLang,
  };
  await db.decks.put(row);
  return { id, created: true };
}
